import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Container, Input, Label, Row, Button, Form, FormFeedback, Alert, Spinner } from 'reactstrap';
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";

import { ToastContainer, toast } from 'react-toastify';
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { createSelector } from 'reselect';
import { useSelector } from "react-redux";

import { useAuth } from '../../hooks/useAuth';
import { useCognito } from '../../hooks/useCognito';
import { clearAuthData } from '../../services/authService';
import { oauthService } from '../../services/oauthService';

import { layoutModeTypes } from "../../Components/constants/layout";
import logoDark from '../../assets/images/exiaLogo_dark.png';
import logoLight from '../../assets/images/exiaLogo_light.png';

const CognitoLogin = () => {
    const navigate = useNavigate();
    const { loginCognito, loginOAuth, isAuthenticated, user, loading: authLoading } = useAuth();
    const { isEnabled: cognitoEnabled, isOAuthEnabled, loading: cognitoLoading } = useCognito();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [authMethod, setAuthMethod] = useState('cognito');

    // Layout state selector
    const selectLayoutState = (state) => state.Layout;
    const layoutData = createSelector(
        selectLayoutState,
        (layout) => ({
            layoutModeType: layout.layoutModeType,
        })
    );
    const { layoutModeType } = useSelector(layoutData);

    // Clear any existing auth data when accessing Cognito login
    useEffect(() => {
        console.log('CognitoLogin - Clearing any existing auth data');
        clearAuthData();
        
        // Also clear any session storage that might be used by the existing auth system
        sessionStorage.removeItem('authUser');
        
        console.log('CognitoLogin - Auth data cleared, ready for fresh login');
    }, []); // Only run once when component mounts

    // Remove the automatic redirect logic since we want users to be able to access this page
    // useEffect(() => {
    //     console.log('CognitoLogin - isAuthenticated:', isAuthenticated);
    //     console.log('CognitoLogin - user:', user);
    //     console.log('CognitoLogin - authLoading:', authLoading);
        
    //     if (isAuthenticated) {
    //         console.log('CognitoLogin - User is authenticated, redirecting to dashboard');
    //         navigate('/dashboard');
    //     } else {
    //         console.log('CognitoLogin - User is not authenticated, staying on login page');
    //     }
    // }, [isAuthenticated, navigate]);

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            email: '',
            password: '',
        },
        validationSchema: Yup.object({
            email: Yup.string().email("Please enter a valid email").required("Please enter your email"),
            password: Yup.string().required("Please enter your password"),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            setError('');

            try {
                if (authMethod === 'cognito') {
                    const result = await loginCognito(values.email, values.password);
                    if (result.success) {
                        toast.success("Login successful!");
                        navigate('/home');
                    }
                }
            } catch (err) {
                console.error('Login error:', err);
                const errorMessage = err.error || err.message || 'Login failed';
                setError(errorMessage);
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        }
    });

    const handleOAuthLogin = () => {
        try {
            loginOAuth();
        } catch (err) {
            console.error('OAuth login error:', err);
            toast.error('OAuth login failed');
        }
    };

    const mode = layoutModeType || layoutModeTypes.LIGHTMODE;

    document.title = "AWS Cognito Login | SeedNations";

    return (
        <React.Fragment>
            <ParticlesAuth>
                <div className="auth-page-content">
                    <Container>
                        <ToastContainer />
                        <Row>
                            <Col lg={12}>
                                <div className="text-center mt-sm-5 mb-4 text-white-50">
                                    <div>
                                        <Link to="/" className="d-inline-block auth-logo">
                                            <img src={mode === layoutModeTypes.LIGHTMODE ? logoLight : logoDark} alt="" height="200" />
                                        </Link>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <Row className="justify-content-center">
                            <Col md={8} lg={6} xl={5}>
                                <Card className="mt-4">
                                    <CardBody className="p-4">
                                        <div className="text-center mt-2">
                                            <h5 className="text-primary">🔐 AWS Cognito Login</h5>
                                            <p className="text-muted">Secure authentication with AWS Cognito</p>
                                        </div>

                                        {error && (
                                            <Alert color="danger" className="mt-3">
                                                {error}
                                            </Alert>
                                        )}

                                        {cognitoLoading && (
                                            <div className="text-center">
                                                <Spinner color="primary" />
                                                <p className="text-muted mt-2">Loading Cognito service...</p>
                                            </div>
                                        )}

                                        {!cognitoLoading && !cognitoEnabled && (
                                            <Alert color="warning">
                                                AWS Cognito service is currently disabled. Please contact support.
                                            </Alert>
                                        )}

                                        {!cognitoLoading && cognitoEnabled && (
                                            <>
                                                {/* Authentication Method Selector */}
                                                <div className="auth-method-selector mb-3">
                                                    <Label className="form-label">Authentication Method</Label>
                                                    <div className="d-flex gap-3">
                                                        <div className="form-check">
                                                            <Input
                                                                type="radio"
                                                                id="cognito-api"
                                                                value="cognito"
                                                                checked={authMethod === 'cognito'}
                                                                onChange={(e) => setAuthMethod(e.target.value)}
                                                                className="form-check-input"
                                                            />
                                                            <Label htmlFor="cognito-api" className="form-check-label">
                                                                🔑 Cognito API
                                                            </Label>
                                                        </div>
                                                        {isOAuthEnabled && (
                                                            <div className="form-check">
                                                                <Input
                                                                    type="radio"
                                                                    id="oauth"
                                                                    value="oauth"
                                                                    checked={authMethod === 'oauth'}
                                                                    onChange={(e) => setAuthMethod(e.target.value)}
                                                                    className="form-check-input"
                                                                />
                                                                <Label htmlFor="oauth" className="form-check-label">
                                                                    🌐 OAuth/OIDC
                                                                </Label>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {authMethod === 'cognito' && (
                                                    <div className="p-2 mt-4">
                                                        <Form
                                                            onSubmit={(e) => {
                                                                e.preventDefault();
                                                                validation.handleSubmit();
                                                                return false;
                                                            }}
                                                        >
                                                            <div className="mb-3">
                                                                <Label htmlFor="email" className="form-label">
                                                                    Email <span className="text-danger">*</span>
                                                                </Label>
                                                                <Input
                                                                    name="email"
                                                                    className="form-control"
                                                                    placeholder="Enter email"
                                                                    type="email"
                                                                    onChange={validation.handleChange}
                                                                    onBlur={validation.handleBlur}
                                                                    value={validation.values.email || ""}
                                                                    invalid={
                                                                        validation.touched.email && validation.errors.email ? true : false
                                                                    }
                                                                />
                                                                {validation.touched.email && validation.errors.email ? (
                                                                    <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                                                                ) : null}
                                                            </div>

                                                            <div className="mb-3">
                                                                <Label className="form-label" htmlFor="password-input">
                                                                    Password <span className="text-danger">*</span>
                                                                </Label>
                                                                <div className="position-relative auth-pass-inputgroup mb-3">
                                                                    <Input
                                                                        name="password"
                                                                        value={validation.values.password || ""}
                                                                        type="password"
                                                                        className="form-control pe-5"
                                                                        placeholder="Enter Password"
                                                                        onChange={validation.handleChange}
                                                                        onBlur={validation.handleBlur}
                                                                        invalid={
                                                                            validation.touched.password && validation.errors.password ? true : false
                                                                        }
                                                                    />
                                                                    {validation.touched.password && validation.errors.password ? (
                                                                        <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                                                                    ) : null}
                                                                </div>
                                                            </div>

                                                            <div className="mt-4">
                                                                <Button
                                                                    color="success"
                                                                    disabled={loading}
                                                                    className="btn btn-success w-100"
                                                                    type="submit"
                                                                >
                                                                    {loading ? (
                                                                        <>
                                                                            <Spinner size="sm" className="me-2" />
                                                                            Signing in...
                                                                        </>
                                                                    ) : (
                                                                        <>🔐 Sign in with Cognito</>
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </Form>
                                                    </div>
                                                )}

                                                {/* OAuth Login Section */}
                                                {authMethod === 'oauth' && isOAuthEnabled && (
                                                    <div className="oauth-section mt-4">
                                                        <div className="text-center">
                                                            <Button
                                                                color="warning"
                                                                className="btn btn-warning w-100"
                                                                onClick={handleOAuthLogin}
                                                            >
                                                                🌐 Login with AWS Cognito (OAuth)
                                                            </Button>
                                                            <p className="text-muted mt-2 small">
                                                                Secure login through AWS Cognito hosted UI with Google integration
                                                            </p>
                                                            
                                                            {/* Test Button for Debugging */}
                                                            <Button
                                                                color="info"
                                                                className="btn btn-info btn-sm mt-2"
                                                                onClick={() => {
                                                                    console.log('=== Testing Frontend Direct OAuth ===');
                                                                    oauthService.testOAuthParams();
                                                                }}
                                                            >
                                                                🧪 Test Frontend Direct OAuth
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Navigation Links */}
                                        <div className="mt-4 text-center">
                                            <div className="signin-other-title">
                                                <h5 className="fs-13 mb-4 title">Other Options</h5>
                                            </div>
                                            <div className="d-flex gap-2 justify-content-center">
                                                <Link to="/login" className="btn btn-outline-primary btn-sm">
                                                    Traditional Login
                                                </Link>
                                                <Link to="/register" className="btn btn-outline-secondary btn-sm">
                                                    Register
                                                </Link>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>

                                {/* Service Status */}
                                {!cognitoLoading && (
                                    <div className="mt-3 text-center">
                                        <div className="text-muted small">
                                            <strong>Service Status:</strong>{' '}
                                            <span className={cognitoEnabled ? 'text-success' : 'text-danger'}>
                                                Cognito: {cognitoEnabled ? '✅ Enabled' : '❌ Disabled'}
                                            </span>
                                            {cognitoEnabled && (
                                                <>
                                                    {' | '}
                                                    <span className={isOAuthEnabled ? 'text-success' : 'text-warning'}>
                                                        OAuth: {isOAuthEnabled ? '✅ Enabled' : '⚠️ Disabled'}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ParticlesAuth>
        </React.Fragment>
    );
};

export default CognitoLogin;